import { useLocalization } from '@/hooks/useLocalization';
import { SearchSearchProductsResponse } from '@winelivery-org/wl-client-sdk-js/src/models/search/product';
import { useFormatter, useTranslations } from 'next-intl';
import { ChevronRight as IconChevronRight } from '../../../../../../../components/icons';
import { Link } from '@/i18n/routing';
import Image from 'next/image';

interface Props {
  q: string;
  suggestions: SearchSearchProductsResponse;
}

export default function Products({ q, suggestions }: Props) {
  const t = useTranslations('common');
  const format = useFormatter();
  const { deliveryType } = useLocalization();
  const tShop = useTranslations('shop');

  if (suggestions.products.items.length === 0) {
    return null;
  }

  return (
    <div>
      <h5 className="text-lg text-greyscale-title font-bold tracking-tighter m-0">
        {tShop('search.products')}
      </h5>
      <ul className="divide-y divide-greyscale-separator m-0 p-0">
        {suggestions.products.items.map((product) => (
          <li key={product.id}>
            <Link
              href={{ pathname: `/shop/[id]`, params: { id: product.slug! } }}
              className="flex items-center gap-3 py-2 -mx-4 px-4 no-underline hover:bg-greyscale-background"
            >
              <div className="h-16 w-16 relative overflow-hidden">
                <Image
                  fill
                  className="object-contain rounded-sm shrink-0"
                  src={product.imageUrl || ''}
                  alt={product.name}
                />
              </div>
              <div className="flex-1 tracking-tight space-y-0 text-greyscale-body overflow-hidden">
                <div className="text-base font-bold leading-5 truncate">
                  {product.name}
                </div>
                <div className="text-sm font-medium leading-5 truncate">
                  {product.producer}
                </div>
                <div className="text-sm font-medium leading-5 truncate text-greyscale-label">
                  {product.country ? product.country.name : ''}
                  {product.region ? `, ${product.region.name}` : ''}
                </div>
                <div className="flex content-center items-center">
                  <div className="text-sm font-bold leading-5 truncate text-greyscale-body mr-2">
                    {format.number(product.price, {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </div>
                  {!product.inStock && (
                    <div
                      className="text-md font-bold leading-5 truncate"
                      style={{ color: '#cf1c44' }}
                    >
                      {t('Availability.NotAvailable')}
                    </div>
                  )}
                  {product.deliveryType === 'ecommerce' &&
                    deliveryType !== 'ecommerce' && (
                      <div
                        className="text-md font-bold leading-5 truncate"
                        style={{ color: '#43d9a3' }}
                      >
                        {t('Availability.AvailableInShipping')}
                      </div>
                    )}
                </div>
              </div>
              <IconChevronRight className="shrink-0 h-5 text-greyscale-body" />
            </Link>
          </li>
        ))}
      </ul>
      {suggestions.products.count > 6 && (
        <Link
          href={{ pathname: `/search-results`, query: { q } }}
          className="text-greyscale-body underline text-base font-medium tracking-tight cursor-pointer"
        >
          {tShop('filter.more')}
        </Link>
      )}
    </div>
  );
}
